import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { reportApiError, correlationIdFromHeaders } from './errorReporter';
import { apiPublic, apiErrorMessage, authJsonHeaders, isPublicPagePath } from './api';
import { DASHBOARD_PATHS, PATHS, buildPath } from "./paths";

// Types
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  /** Optional first name — used as the chat sender label so messages read
   *  "Sarah" instead of "QA". Empty string when not set. */
  firstName?: string;
  isAdmin: boolean;
}

export interface Organization {
  id: string;
  name: string;
  role: string;
  createdAt: string;
}

export interface ProjectSsoConfig {
  GoogleWebClientId:     string;
  GoogleClientIdIos:     string;
  AppleBundleId:         string;
  AppleServicesId:       string;
  AppleRedirectUri:      string;
  CustomAuthWebhookUrl?:    string;
  CustomAuthWebhookSecret?: string;
  CustomAuthProviderLabel?: string;
}

export interface Project {
  id: string;
  name: string;
  orgId: string;
  /** Optional one-level parent. NULL for top-level projects. */
  ParentProjectId?: string | null;
  /** App icon URL. Populated automatically from APK/IPA on first build
   *  upload, or set manually via Project Settings → Icon. */
  IconUrl?: string | null;
  /** Per-platform icon URLs (latest build wins per platform). Keys are
   *  lowercased platform names: 'android' | 'ios' | 'windows' | ... */
  IconUrls?: Record<string, string> | null;
  /** Per-project SDK SSO OAuth client config. Drives in-game Google /
   *  Apple sign-in. Empty strings = provider disabled. */
  SsoConfig?: ProjectSsoConfig | null;
  createdAt: string;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  orgs: Organization[];
  activeOrg: Organization | null;
  projects: Project[];
  loading: boolean;
  // True if the user is admin of the currently-active org, or a global admin.
  // Mirrors the server's `requireOrgAdmin` gate so UI hides match backend denies.
  isOrgAdmin: boolean;
  // True if the user is QA-or-above (qa / admin / owner) of the active org,
  // or a global admin. Mirrors `requireOrgQa` on the server.
  isOrgQa: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, isSetup?: boolean) => Promise<void>;
  logout: () => void;
  setActiveOrg: (org: Organization) => void;
  /** Patch the active org in-place (e.g. after rename). Does not clear
   *  project filters or re-select the org — use setActiveOrg for that. */
  updateActiveOrg: (patch: Partial<Organization>) => void;
  refreshOrgs: () => Promise<Organization[]>;
  refreshProjects: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Standard Context+Provider+hook trio. Splitting hook into its own file is
// the by-the-book Fast Refresh fix but would touch every page/component
// that calls `useAuth()` (50+ sites). Co-locating with the Provider keeps
// the public surface stable.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Auth flows route through `apiPublic` (no auto-redirect) so this module keeps
// full control of the 401 policy: only an explicit token failure on `/me`
// logs out — a tokenless `/me` probe is how local-dev auth discovers the
// server-side bypass, and a failed token refresh must NOT end the session.
async function authFetch<T>(url: string, options?: { method?: string; body?: BodyInit; headers?: Record<string, string> }): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { ...authJsonHeaders(), ...(options?.headers ?? {}) };
  const activeOrgId = localStorage.getItem('active_org_id');
  if (activeOrgId) headers['X-Org-Id'] = activeOrgId;

  try {
    return await apiPublic<T>(url, { method: options?.method, body: options?.body, headers });
  } catch (e) {
    const status = (e as { statusCode?: number }).statusCode ?? 0;
    if (status === 401 && url === DASHBOARD_PATHS.authMe) {
      if (token) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('active_org_id');
        localStorage.removeItem('active_project_id');
        // Don't eject the visitor to /login on PUBLIC routes — a share link,
        // docs, or the marketing site must keep working when an old/expired
        // token is sitting in localStorage. Clear the dead token (above) and
        // fall through to render the public page. Dashboard routes still
        // redirect so a stale session re-authenticates.
        if (!isPublicPagePath(window.location.pathname)) window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }
    const message = apiErrorMessage(e);
    reportApiError({
      url,
      status,
      message,
      method: (options?.method ?? 'GET').toUpperCase(),
      requestId: correlationIdFromHeaders((e as { response?: Response }).response?.headers),
    });
    throw new Error(message);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [activeOrg, setActiveOrgState] = useState<Organization | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshOrgs = useCallback(async () => {
    try {
      const data = await authFetch<Organization[]>(DASHBOARD_PATHS.orgs);
      setOrgs(data);
      return data;
    } catch {
      setOrgs([]);
      return [];
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    if (!activeOrg) {
      setProjects([]);
      return;
    }
    try {
      const data = await authFetch<Array<Record<string, unknown>>>(buildPath(DASHBOARD_PATHS.orgsByOrgIdProjects, { orgId: activeOrg.id }));
      // Normalize Parse field names (capital) to our interface (lowercase)
      setProjects(data.map(p => ({
        id: String(p.id || p.Id || p.objectId || ''),
        name: String(p.name || p.Name || ''),
        orgId: String(p.orgId || p.OrgId || ''),
        ParentProjectId: (p.ParentProjectId ?? p.parentProjectId ?? null) as string | null,
        IconUrl: (p.IconUrl ?? p.iconUrl ?? null) as string | null,
        IconUrls: (p.IconUrls ?? p.iconUrls ?? null) as Record<string, string> | null,
        SsoConfig: (p.SsoConfig ?? p.ssoConfig ?? null) as ProjectSsoConfig | null,
        createdAt: String(p.createdAt || ''),
      })));
    } catch {
      setProjects([]);
    }
  }, [activeOrg]);

  // Load projects when active org changes. Initial call deferred via
  // queueMicrotask so the setProjects inside `refreshProjects` lands in a
  // callback rather than the effect body.
  useEffect(() => {
    let cancelled = false;
    if (activeOrg && user) {
      queueMicrotask(() => {
        if (cancelled) return;
        refreshProjects();
      });
    }
    return () => { cancelled = true; };
  }, [activeOrg?.id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore session on mount. In local dev the server may provide a dashboard
  // identity without a JWT, so /me is probed even when localStorage has no token.
  useEffect(() => {
    (async () => {
      type SessionResponse = {
        user: AuthUser;
        organizations: Organization[];
      };
      const applySession = (me: SessionResponse) => {
        setUser(me.user);
        setOrgs(me.organizations);

        // Restore active org — auto-select first if none saved or saved ID is invalid
        const savedOrgId = localStorage.getItem('active_org_id');
        const org = (savedOrgId && savedOrgId !== 'undefined')
          ? me.organizations.find(o => o.id === savedOrgId) || me.organizations[0] || null
          : me.organizations[0] || null;
        if (org) {
          setActiveOrgState(org);
          localStorage.setItem('active_org_id', org.id);
        }
      };

      try {
        applySession(await authFetch<SessionResponse>(DASHBOARD_PATHS.authMe));
      } catch {
        setToken(null);
        localStorage.removeItem('auth_token');
        try {
          applySession(await authFetch<SessionResponse>(DASHBOARD_PATHS.authMe));
        } catch {
          // No token and no local dev bypass.
        }
      }
      setLoading(false);
    })();
  }, []);  

  // Auto-refresh token every 24 hours to prevent session expiry
  useEffect(() => {
    if (!token) return;
    const REFRESH_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
    const refresh = async () => {
      try {
        const data = await authFetch<{ token: string }>(DASHBOARD_PATHS.authRefresh, { method: 'POST' });
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
          setToken(data.token);
        }
      } catch { /* ignore — will retry next interval */ }
    };
    const i = setInterval(refresh, REFRESH_INTERVAL);
    return () => clearInterval(i);
  }, [token]);  


  const login = async (email: string, password: string) => {
    const data = await authFetch<{
      token: string;
      user: AuthUser;
      organizations: Organization[];
    }>(PATHS.authLogin, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    localStorage.setItem('auth_token', data.token);
    setToken(data.token);
    setUser(data.user);
    setOrgs(data.organizations);

    const org = data.organizations[0] || null;
    if (org) {
      setActiveOrgState(org);
      localStorage.setItem('active_org_id', org.id);
    }
  };

  const register = async (email: string, password: string, displayName: string, isSetup = false) => {
    const endpoint = isSetup ? PATHS.authSetup : PATHS.authRegister;
    const data = await authFetch<{
      token: string;
      user: AuthUser;
      organizations: Organization[];
    }>(endpoint, {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName }),
    });

    localStorage.setItem('auth_token', data.token);
    setToken(data.token);
    setUser(data.user);
    setOrgs(data.organizations);

    const org = data.organizations[0] || null;
    if (org) {
      setActiveOrgState(org);
      localStorage.setItem('active_org_id', org.id);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('active_org_id');
    localStorage.removeItem('active_project_id');
    setToken(null);
    setUser(null);
    setOrgs([]);
    setActiveOrgState(null);
    setProjects([]);
  };

  const setActiveOrg = (org: Organization) => {
    setActiveOrgState(org);
    localStorage.setItem('active_org_id', org.id);
    // Clear all per-page project filters + the legacy active_project_id mirror
    // when the org changes — projects don't span orgs.
    localStorage.removeItem('active_project_id');
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('project_filter_')) localStorage.removeItem(key);
    }
  };

  const updateActiveOrg = (patch: Partial<Organization>) => {
    setActiveOrgState((prev) => (prev ? { ...prev, ...patch } : prev));
    setOrgs((prev) => prev.map((o) => (activeOrg && o.id === activeOrg.id ? { ...o, ...patch } : o)));
  };

  const isOrgAdmin = !!user?.isAdmin || activeOrg?.role === 'admin';
  const isOrgQa = isOrgAdmin || activeOrg?.role === 'qa';

  return (
    <AuthContext.Provider value={{
      user, token, orgs, activeOrg, projects, loading,
      isOrgAdmin, isOrgQa,
      login, register, logout, setActiveOrg, updateActiveOrg,
      refreshOrgs, refreshProjects,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
